import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useFocusEffect } from 'expo-router';
import { useCallback, useRef } from 'react';
import { AppState, type LayoutChangeEvent, type NativeScrollEvent } from 'react-native';

import {
  trackCommunityPostRead,
  type CommunityPostOpenedVia,
  type PostFormat,
} from '@/util/analyticsEvents';
import {
  createCommunityPostReadSession,
  type CommunityPostReadExitReason,
  type CommunityPostReadSession,
} from '@/util/communityPostReadSession';

type ActiveRead = {
  session: CommunityPostReadSession;
  postId: number;
  openedVia: CommunityPostOpenedVia;
  resumed: boolean;
  format: PostFormat | undefined;
};

/** Pointer and key activity on a desktop page that never reaches the scroll view. */
const WEB_ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'keydown'] as const;

/**
 * Times how long a post is actually read and how far down it the reader gets,
 * and reports it as `Community post read` when the reading stops.
 *
 * A read runs while the screen is focused, the post has loaded, and the app or
 * tab is in the foreground. React Native Web reports a hidden tab through
 * `AppState` too, so one listener covers both platforms, and closing the tab
 * counts as backgrounding it. The event goes out as the app leaves the
 * foreground rather than on a later return, since a closed tab or a killed app
 * never returns.
 *
 * Paging with Previous or Next swaps the post under a screen that stays
 * mounted, so the post id is a dependency of the focus effect: the old read
 * ends as the new one begins. The cleanup tells a page from a departure by
 * comparing the read's post with the one now on screen, which is why the
 * current post id is held in a ref assigned during render.
 *
 * Wire the returned handlers onto the post's scroll view, and call
 * `onAnswerEnd` with where the answer stops and the comments begin.
 */
export function useCommunityPostReadTracking({
  postId,
  isReady,
  openedVia,
  format,
}: {
  postId: number;
  isReady: boolean;
  openedVia: CommunityPostOpenedVia;
  /** The layout the post is shown in, taken when a read starts so paging cannot swap it. */
  format?: PostFormat;
}) {
  const activeReadRef = useRef<ActiveRead | null>(null);

  const currentPostIdRef = useRef(postId);

  currentPostIdRef.current = postId;

  const openedViaRef = useRef(openedVia);

  openedViaRef.current = openedVia;

  const formatRef = useRef(format);

  formatRef.current = format;

  const scrollOffsetRef = useRef(0);

  const viewportHeightRef = useRef(0);

  const answerEndRef = useRef<number | null>(null);

  const startRead = useCallback((resumed: boolean) => {
    const session = createCommunityPostReadSession(Date.now());

    session.recordVisibleBottom(scrollOffsetRef.current + viewportHeightRef.current);

    if (answerEndRef.current !== null) {
      session.recordAnswerEnd(answerEndRef.current);
    }

    activeReadRef.current = {
      session,
      postId: currentPostIdRef.current,
      openedVia: openedViaRef.current,
      resumed,
      format: formatRef.current,
    };
  }, []);

  const endRead = useCallback((exitReason: CommunityPostReadExitReason) => {
    const activeRead = activeReadRef.current;

    if (!activeRead) {
      return;
    }

    activeReadRef.current = null;

    trackCommunityPostRead({
      postId: activeRead.postId,
      openedVia: activeRead.openedVia,
      exitReason,
      resumed: activeRead.resumed,
      result: activeRead.session.finish(Date.now()),
      format: activeRead.format,
    });
  }, []);

  const recordActivity = useCallback(() => {
    activeReadRef.current?.session.recordActivity(Date.now());
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (!isReady) {
        return;
      }

      const readPostId = currentPostIdRef.current;

      startRead(false);

      const appStateSubscription = AppState.addEventListener('change', (state) => {
        if (state === 'background') {
          endRead('backgrounded');
        }

        if (state === 'active' && !activeReadRef.current) {
          startRead(true);
        }
      });

      const canListenToDocument = isWeb && typeof document !== 'undefined';

      if (canListenToDocument) {
        WEB_ACTIVITY_EVENTS.forEach((eventName) =>
          document.addEventListener(eventName, recordActivity, { passive: true })
        );
      }

      return () => {
        appStateSubscription.remove();

        if (canListenToDocument) {
          WEB_ACTIVITY_EVENTS.forEach((eventName) =>
            document.removeEventListener(eventName, recordActivity)
          );
        }

        const switchedPost = currentPostIdRef.current !== readPostId;

        endRead(switchedPost ? 'switched_post' : 'left');

        if (switchedPost) {
          scrollOffsetRef.current = 0;

          answerEndRef.current = null;
        }
      };
    }, [postId, isReady, startRead, endRead, recordActivity])
  );

  const recordVisibleBottom = () => {
    activeReadRef.current?.session.recordVisibleBottom(
      scrollOffsetRef.current + viewportHeightRef.current
    );
  };

  const onScroll = ({ nativeEvent }: { nativeEvent: NativeScrollEvent }) => {
    scrollOffsetRef.current = nativeEvent.contentOffset.y;

    viewportHeightRef.current = nativeEvent.layoutMeasurement.height;

    recordVisibleBottom();

    recordActivity();
  };

  const onLayout = (event: LayoutChangeEvent) => {
    viewportHeightRef.current = event.nativeEvent.layout.height;

    recordVisibleBottom();
  };

  const onAnswerEnd = (y: number) => {
    answerEndRef.current = y;

    activeReadRef.current?.session.recordAnswerEnd(y);
  };

  return { onScroll, onLayout, onTouchStart: recordActivity, onAnswerEnd };
}
